import React from 'react';

import { useSelector } from 'react-redux';

import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { BlockerIcon } from 'src/components/icon';
import { ParticipantState, ParticipantStateTypes } from 'src/components/types';
import { UserAvatar } from 'src/containers/user-avatar';
import { UserName } from 'src/containers/user-name';
import { useIntl } from 'src/hooks/intl';
import { useCheckUserAccess } from 'src/hooks/use-check-user-access';
import {
  getIsCurrentPullRequestOpen,
  getIsPullRequestRemoveReviewerPending,
} from 'src/redux/pull-request/selectors';
import {
  getAccountIdFromUuid,
  getCurrentUserUuid,
} from 'src/selectors/user-selectors';
import { BucketState } from 'src/types/state';

import { GrayIcon } from './gray-icon';
import * as styles from './reviewer-status.style';
import messages from './reviewer-statuses-card.i18n';

export const ReviewerStatus = ({
  user,
  state,
  canDeleteReviewersOverride,
  onDelete,
}: {
  user: BB.User | null | undefined;
  state: ParticipantState;
  canDeleteReviewersOverride?: boolean;
  onDelete: (userId: string) => void;
}) => {
  const isOpenPr = useSelector(getIsCurrentPullRequestOpen);
  const currentUserUuid = useSelector(getCurrentUserUuid);
  const isPullRequestRemoveReviewerPending = useSelector(
    getIsPullRequestRemoveReviewerPending
  );
  const currentUserAccountId =
    useSelector<BucketState>(bitbucketState =>
      getAccountIdFromUuid(bitbucketState, user?.uuid as string)
    ) || user?.account_id;

  const intl = useIntl();
  const userHasAccess = useCheckUserAccess(
    user?.uuid === currentUserUuid ? 'read' : 'write'
  );

  const canRemoveReviewer =
    canDeleteReviewersOverride !== undefined
      ? canDeleteReviewersOverride
      : isOpenPr && userHasAccess;

  if (!user) return null;

  const renderStatusIcon = () => {
    if (state === ParticipantStateTypes.changesRequested) {
      return (
        <BlockerIcon
          primaryColor={token('color.icon.warning')}
          label={intl.formatMessage(messages.changesRequested)}
        />
      );
    } else if (state === ParticipantStateTypes.approved) {
      return (
        <CheckCircleIcon
          size="medium"
          primaryColor={token('color.icon.success')}
          label={intl.formatMessage(messages.approved)}
        />
      );
    }
    return <GrayIcon />;
  };

  const handleRemoveReviewer = () => {
    if (typeof currentUserAccountId === 'string') {
      onDelete(currentUserAccountId);
    }
  };

  return (
    <styles.ReviewerStatusContainer
      tabIndex={0}
      canRemoveReviewer={canRemoveReviewer}
    >
      <styles.BackgroundContainer>
        <styles.IconWrapper>{renderStatusIcon()}</styles.IconWrapper>
        <styles.UserAvatarWrapper>
          <UserAvatar
            profileCardPosition="left-start"
            user={user}
            size="small"
          />
        </styles.UserAvatarWrapper>
        <styles.NameWrapper>
          <UserName profileCardPosition="left-start" user={user} />
        </styles.NameWrapper>
      </styles.BackgroundContainer>
      {canRemoveReviewer && (
        <styles.RemoveReviewerButton
          onClick={handleRemoveReviewer}
          testId="remove-reviewer-button"
          appearance="subtle"
          spacing="none"
          iconBefore={
            isPullRequestRemoveReviewerPending ? (
              <Spinner size="medium" delay={0} />
            ) : (
              <EditorCloseIcon
                label={intl.formatMessage(messages.removeReviewer)}
              />
            )
          }
        />
      )}
    </styles.ReviewerStatusContainer>
  );
};

export default React.memo(ReviewerStatus);
